import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
    Activity, Gauge, Clock, AlertTriangle, CheckCircle, RefreshCw,
    TrendingUp, Zap, Server, Shield, ArrowUpRight, ArrowDownRight,
    Minus
} from 'lucide-react';

import api from '../../lib/api';

const fadeIn = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } };

function getLatencyColor(ms) {
    if (ms <= 100) return 'text-green-400';
    if (ms <= 300) return 'text-emerald-400';
    if (ms <= 500) return 'text-amber-400';
    return 'text-red-400';
}

function getLatencyBg(ms) {
    if (ms <= 100) return 'bg-green-500/10 border-green-500/20';
    if (ms <= 300) return 'bg-emerald-500/10 border-emerald-500/20';
    if (ms <= 500) return 'bg-amber-500/10 border-amber-500/20';
    return 'bg-red-500/10 border-red-500/20';
}

function HealthGauge({ score, status }) {
    const color = score >= 75 ? '#22c55e' : score >= 50 ? '#f59e0b' : '#ef4444';
    const label = status === 'healthy' ? 'Sain' : status === 'degraded' ? 'Dégradé' : 'Critique';
    const circumference = 2 * Math.PI * 42;
    const progress = (score / 100) * circumference;

    return (
        <div className="flex flex-col items-center">
            <div className="relative w-28 h-28">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                    <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth="6" className="text-slate-800" />
                    <circle
                        cx="50" cy="50" r="42" fill="none"
                        stroke={color} strokeWidth="6" strokeLinecap="round"
                        strokeDasharray={circumference}
                        strokeDashoffset={circumference - progress}
                        style={{ transition: 'stroke-dashoffset 1s ease' }}
                    />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-black" style={{ color }}>{score}</span>
                    <span className="text-[10px] text-slate-400 font-medium">/ 100</span>
                </div>
            </div>
            <Badge className={`mt-2 text-xs ${score >= 75 ? 'bg-green-500/15 text-green-400 border-green-500/20' : score >= 50 ? 'bg-amber-500/15 text-amber-400 border-amber-500/20' : 'bg-red-500/15 text-red-400 border-red-500/20'}`}>
                {score >= 75 ? <CheckCircle className="w-3 h-3 mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
                {label}
            </Badge>
        </div>
    );
}

function LatencyBar({ value, max }) {
    const pct = Math.min((value / max) * 100, 100);
    const color = value <= 100 ? 'bg-green-500' : value <= 300 ? 'bg-emerald-500' : value <= 500 ? 'bg-amber-500' : 'bg-red-500';
    return (
        <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
        </div>
    );
}

export default function MonitoringTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(true);

    const loadData = useCallback(async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true);
        try {
            const res = await api.get('/internal/monitoring/latency');
            setData(res.data);
        } catch (err) {
            console.error('Monitoring fetch error:', err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    useEffect(() => {
        if (!autoRefresh) return;
        const interval = setInterval(() => loadData(true), 15000);
        return () => clearInterval(interval);
    }, [autoRefresh, loadData]);

    if (loading || !data) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="animate-spin w-8 h-8 border-2 border-[#681793] border-t-transparent rounded-full" />
            </div>
        );
    }

    const { global: g, endpoints, slow_endpoints, error_endpoints } = data;
    const maxP95 = Math.max(...endpoints.map(e => e.p95_ms), 500);

    return (
        <motion.div className="space-y-6" initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.06 } } }} data-testid="monitoring-tab">

            {/* Header */}
            <motion.div variants={fadeIn} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#681793]/20 flex items-center justify-center">
                        <Activity className="w-5 h-5 text-[#681793]" />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-white">Monitoring API</h2>
                        <p className="text-xs text-slate-400">Latence en temps réel</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAutoRefresh(!autoRefresh)}
                        className={`border-slate-700 text-xs ${autoRefresh ? 'text-green-400' : 'text-slate-400'}`}
                        data-testid="toggle-auto-refresh"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${autoRefresh ? 'animate-spin' : ''}`} style={{ animationDuration: '3s' }} />
                        {autoRefresh ? 'Auto 15s' : 'Pause'}
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => loadData(true)}
                        disabled={refreshing}
                        className="border-slate-700 text-xs"
                        data-testid="manual-refresh"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
            </motion.div>

            {/* Health + Global Stats */}
            <motion.div variants={fadeIn} className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                {/* Health gauge */}
                <div className="lg:col-span-1 bg-slate-900 rounded-2xl border border-slate-800 p-5 flex flex-col items-center justify-center">
                    <HealthGauge score={data.health_score} status={data.status} />
                    <div className="mt-3 flex items-center gap-1.5 text-xs text-slate-400">
                        <Server className="w-3 h-3" />
                        <span>Uptime: {data.uptime_human}</span>
                    </div>
                </div>

                {/* Stats cards */}
                <div className="lg:col-span-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-slate-900 rounded-xl border border-slate-800 p-4" data-testid="stat-requests">
                        <div className="flex items-center gap-2 mb-2">
                            <Zap className="w-4 h-4 text-blue-400" />
                            <span className="text-xs text-slate-400">Total requêtes</span>
                        </div>
                        <p className="text-2xl font-bold text-white">{g.total_requests.toLocaleString()}</p>
                        <p className="text-xs text-slate-500 mt-1">{g.throughput_rps} req/s</p>
                    </div>
                    <div className="bg-slate-900 rounded-xl border border-slate-800 p-4" data-testid="stat-avg">
                        <div className="flex items-center gap-2 mb-2">
                            <Gauge className="w-4 h-4 text-[#681793]" />
                            <span className="text-xs text-slate-400">Latence moyenne</span>
                        </div>
                        <p className={`text-2xl font-bold ${getLatencyColor(g.avg_ms)}`}>{g.avg_ms}<span className="text-sm font-normal ml-0.5">ms</span></p>
                    </div>
                    <div className="bg-slate-900 rounded-xl border border-slate-800 p-4" data-testid="stat-p95">
                        <div className="flex items-center gap-2 mb-2">
                            <TrendingUp className="w-4 h-4 text-[#FF914D]" />
                            <span className="text-xs text-slate-400">P95</span>
                        </div>
                        <p className={`text-2xl font-bold ${getLatencyColor(g.p95_ms)}`}>{g.p95_ms}<span className="text-sm font-normal ml-0.5">ms</span></p>
                        <p className="text-xs text-slate-500 mt-1">P99: {g.p99_ms}ms</p>
                    </div>
                    <div className="bg-slate-900 rounded-xl border border-slate-800 p-4" data-testid="stat-errors">
                        <div className="flex items-center gap-2 mb-2">
                            <Shield className="w-4 h-4 text-red-400" />
                            <span className="text-xs text-slate-400">Taux d'erreur</span>
                        </div>
                        <p className={`text-2xl font-bold ${g.error_rate > 1 ? 'text-red-400' : 'text-green-400'}`}>{g.error_rate}<span className="text-sm font-normal ml-0.5">%</span></p>
                        <p className="text-xs text-slate-500 mt-1">{g.total_errors} erreurs</p>
                    </div>
                </div>
            </motion.div>

            {/* Slow endpoints alert */}
            {slow_endpoints.length > 0 && (
                <motion.div variants={fadeIn}>
                    <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="w-4 h-4 text-amber-400" />
                            <span className="text-sm font-semibold text-amber-300">{slow_endpoints.length} endpoint(s) lent(s) (P95 &gt; 300ms)</span>
                        </div>
                        <div className="space-y-1.5">
                            {slow_endpoints.slice(0, 5).map((e, i) => (
                                <div key={i} className="flex items-center justify-between text-xs">
                                    <code className="text-amber-200/80 font-mono truncate max-w-[70%]">{e.endpoint}</code>
                                    <span className="text-amber-400 font-semibold">{e.p95_ms}ms</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Endpoints table */}
            <motion.div variants={fadeIn}>
                <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
                    <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-slate-400" />
                            <h3 className="font-semibold text-white text-sm">Latence par endpoint</h3>
                        </div>
                        <Badge className="bg-slate-800 text-slate-300 border-slate-700 text-xs">
                            {endpoints.length} endpoints
                        </Badge>
                    </div>

                    {/* Table header */}
                    <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-2 text-xs text-slate-500 font-medium border-b border-slate-800/50">
                        <div className="col-span-5">Endpoint</div>
                        <div className="col-span-1 text-right">Req.</div>
                        <div className="col-span-1 text-right">Avg</div>
                        <div className="col-span-1 text-right">P50</div>
                        <div className="col-span-1 text-right">P95</div>
                        <div className="col-span-1 text-right">P99</div>
                        <div className="col-span-2">Distribution</div>
                    </div>

                    {/* Table rows */}
                    <div className="divide-y divide-slate-800/50 max-h-[500px] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                        {endpoints.map((e, i) => (
                            <div key={i} className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center hover:bg-slate-800/30 transition-colors text-xs" data-testid={`endpoint-row-${i}`}>
                                <div className="col-span-12 sm:col-span-5 flex items-center gap-2 min-w-0">
                                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${e.p95_ms <= 100 ? 'bg-green-500' : e.p95_ms <= 300 ? 'bg-emerald-500' : e.p95_ms <= 500 ? 'bg-amber-500' : 'bg-red-500'}`} />
                                    <code className="text-slate-300 font-mono truncate">{e.endpoint}</code>
                                    {e.error_rate > 0 && (
                                        <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-[9px] px-1 py-0 shrink-0">
                                            {e.error_rate}%
                                        </Badge>
                                    )}
                                </div>
                                <div className="hidden sm:block col-span-1 text-right text-slate-400">{e.count}</div>
                                <div className={`hidden sm:block col-span-1 text-right font-medium ${getLatencyColor(e.avg_ms)}`}>{e.avg_ms}</div>
                                <div className={`hidden sm:block col-span-1 text-right ${getLatencyColor(e.p50_ms)}`}>{e.p50_ms}</div>
                                <div className={`hidden sm:block col-span-1 text-right font-semibold ${getLatencyColor(e.p95_ms)}`}>{e.p95_ms}</div>
                                <div className={`hidden sm:block col-span-1 text-right ${getLatencyColor(e.p99_ms)}`}>{e.p99_ms}</div>
                                <div className="hidden sm:block col-span-2 pr-2">
                                    <LatencyBar value={e.p95_ms} max={maxP95} />
                                </div>
                                {/* Mobile compact view */}
                                <div className="col-span-12 sm:hidden flex items-center justify-between mt-1">
                                    <span className="text-slate-500">{e.count} req</span>
                                    <div className="flex items-center gap-3">
                                        <span className="text-slate-500">avg <span className={getLatencyColor(e.avg_ms)}>{e.avg_ms}ms</span></span>
                                        <span className="text-slate-500">p95 <span className={`font-semibold ${getLatencyColor(e.p95_ms)}`}>{e.p95_ms}ms</span></span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Legend */}
                    <div className="px-4 py-3 border-t border-slate-800 flex items-center gap-4 text-[10px] text-slate-500">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> &le; 100ms</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> &le; 300ms</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> &le; 500ms</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> &gt; 500ms</span>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
}
